import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AdminDashboard } from '../../components/AdminDashboard'
import { useAuth } from '../../contexts/AuthContext'
import { useClientProfile } from '../../hooks/useClientProfile'
import { AppLayout } from '../../components/layouts/AppLayout'
import BuyCreditsModal from '../../components/modals/BuyCreditsModal'
import InvoicesModal from '../../components/modals/InvoicesModal'
import { queryKeys } from '../../services/query/queryKeys'

export const Route = createFileRoute('/admin')({
  beforeLoad: ({ context }) => {
    // Only super admin can access this route (label check + email fallback for safety)
    const isAdmin =
      context.auth?.user?.labels?.includes('superadmin') ||
      context.auth?.user?.email === 'brainiac@brainiacsbots.com'
    if (!isAdmin) {
      throw redirect({ to: '/campaigns' })
    }
  },
  component: AdminPage,
})

function AdminPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setTargetEmail } = useAuth()
  // Don't pass email override - let the hook use targetEmail || currentUser?.email internally
  // This ensures impersonation works correctly in the header
  const clientQuery = useClientProfile()
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false)
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false)

  // Handle client selection from admin dashboard
  const handleClientSelect = (clientEmail: string) => {
    console.log('🎭 Starting impersonation for:', clientEmail)

    // Remove cached target-user data so impersonation always starts from a fresh fetch.
    queryClient.removeQueries({ queryKey: queryKeys.campaigns.all(clientEmail) })
    queryClient.removeQueries({ queryKey: queryKeys.profile.client(clientEmail), exact: true })

    // Set the target email for impersonation
    setTargetEmail(clientEmail)

    // Mark any late subscribers stale as a secondary guard.
    queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.list(clientEmail) })
    queryClient.invalidateQueries({ queryKey: queryKeys.profile.client(clientEmail) })

    // Navigate to campaigns with impersonated context
    navigate({ to: '/campaigns' })
  }

  return (
    <AppLayout
      currentView="admin"
      onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      onViewInvoices={() => setIsInvoicesModalOpen(true)}
      aiCredits={clientQuery.data?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      clientInfo={clientQuery.data}
    >
      <main
        className="app-page-offset min-h-screen pt-28 animate-in fade-in duration-300"
        style={{ animation: 'fadeIn 0.3s ease-in-out' }}
      >
        <AdminDashboard onClientSelect={handleClientSelect} />
      </main>

      {/* Buy Credits Modal */}
      <BuyCreditsModal isOpen={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />

      {/* Invoices Modal */}
      <InvoicesModal isOpen={isInvoicesModalOpen} onClose={() => setIsInvoicesModalOpen(false)} />
    </AppLayout>
  )
}
